import { postField, type ProjectPost } from '../../constants/project-posts'

import { type ProjectNotificationEvent } from '@brickup/shared/constants'
import { COMMON } from './common'
import { USER_ROLE_LABELS } from './enum-labels'
import { FIELD_LABELS } from './fields'
import { TERMS } from './terms'

const PROJECT_NOTES = 'Gemeinsame Notizen'

const PROJECT_FALLBACK_NAME = (id: number) => `Projekt #${id}`

export const PROJECTS = {
  removePictureMessage:
    'Das Bild wird dauerhaft gelöscht und ist danach auf keiner Ansicht des Objekts mehr zu sehen.',
  floorCountMismatch: (planned: number, recorded: number) =>
    `Weicht von der Planung ab: dort sind ${planned} ${TERMS.fullFloor}e angelegt, hier stehen ${recorded}.`,
  create: 'Projekt erstellen',
  /** The "no one" row of the customer picker. */
  noCustomerOption: `– Kein ${TERMS.customer} –`,
  /** A project with no name of its own, in a picker. */
  fallbackName: PROJECT_FALLBACK_NAME,
  /** What a project is called on screen. A name that was never filled in is
   *  an empty string, which reads as a missing row. */
  displayName: (project: { id: number; name: string | null }) =>
    project.name || PROJECT_FALLBACK_NAME(project.id),
  /** Names the notes pane every project shows beside its tabs. */
  notes: PROJECT_NOTES,
  /** Heads the notes pane with what the notes are about. */
  notesHeading: (project: string) => `${PROJECT_NOTES} ${project}`,
  /** Heads the block that says whose project this is and where it stands. */
  assignment: 'Zuordnung',
  openAsCustomer: 'Als Kunde öffnen',
  /** One rule, read by both controls it closes and by the server that
   *  rejects the combination a request got past them. */
  publicCustomerConflict: `Ein Projekt ist entweder öffentlich oder einem ${TERMS.customer} zugewiesen — beides zusammen geht nicht.`,
  /**
   * Who works on a project beside the one responsible for it. The tab on the
   * project, the badge on a list row found through them, and the server's
   * refusals.
   */
  access: {
    title: 'Zugriff',
    /** A person who works on the project without holding one of its posts. */
    collaborator: 'Mitarbeit',
    description:
      'Diese Personen können das Projekt bearbeiten. Alle anderen können es nur ansehen.',
    manageHint:
      'Wer Zugriff bekommt, entscheiden der Berater und die Administration.',
    noResponsible: 'Niemand eingetragen',
    add: 'Zugriff geben',
    addPlaceholder: 'Mitarbeiter suchen …',
    remove: 'Zugriff entziehen',
    removeConfirm: {
      title: 'Zugriff entziehen',
      message: (name: string) =>
        `${name} kann das Projekt danach nur noch lesen.`,
    },
    errors: {
      notSales: `Zugriff bekommen nur ${USER_ROLE_LABELS.SALES}, ${USER_ROLE_LABELS.PROJECT_MANAGER} und ${USER_ROLE_LABELS.SITE_MANAGER}.`,
      isResponsible: 'Diese Person ist auf dem Projekt schon eingetragen.',
      responsibleOnly: 'Das können nur der Berater und die Administration.',
    },
  },

  /**
   * The three posts a project names somebody for. Each is a field of the
   * project and carries the field's own label, so the only word needed here is
   * what an empty picker says — and `Kein` is the same before all three nouns,
   * which is why this reads them rather than writing them out again. A post
   * whose noun is feminine makes it a literal per post.
   */
  posts: {
    none: (post: ProjectPost) => `Kein ${FIELD_LABELS[postField(post)].label}`,
  },
  /** A project another advisor is responsible for: read from every screen,
   *  changed from none. Both the banner over the screen and the sentence the
   *  server answers a request that got past it with. */
  notAssigned: {
    title: 'Dieses Projekt ist anders besetzt',
    hint: 'Du kannst alles lesen. Ändern können es, wer auf dem Projekt steht, wer Zugriff bekommen hat, und die Administration.',
    unassignedTitle: 'Niemand ist eingetragen',
    unassignedHint:
      'Solange niemand auf dem Projekt steht, ändert es nur die Administration.',
    refused:
      'Du stehst auf diesem Projekt nicht und hast auch keinen Zugriff bekommen. Es wurde nichts geändert.',
    /** In the banner over the customer's own screens, watched from inside. */
    watchingOnly: 'Nur ansehen — dieses Projekt ist anders besetzt.',
    contactPersonLocked:
      'Die Besetzung entscheiden der Berater und die Administration.',
  },
  /** In each post's filter: the projects that post names nobody for. */
  filterPostNone: 'Niemand eingetragen',
  /** The switch beside the three post filters. Not one of them: it matches
   *  every post and the access given beside them. */
  filterMine: 'Meine Projekte',
  filterCustomerNone: 'Ohne Kunden',
  filterStatusActive: COMMON.active,
  noProjectsFound: 'Keine Projekte gefunden',
  searchProjects: 'Projekte suchen…',
  customerUnsubscribedBadge: 'E-Mails abbestellt',
  notifyCustomer: {
    title: 'Kunden benachrichtigen?',
    reason: {
      'project-assigned':
        'Das Projekt ist jetzt einem Kunden zugewiesen. Soll er per E-Mail erfahren, dass es für ihn bereitsteht?',
      'configurator-open':
        'Der Konfigurator ist jetzt freigegeben. Soll der Kunde per E-Mail erfahren, dass er seine Sanierung zusammenstellen kann?',
    } satisfies Record<ProjectNotificationEvent, string>,
    messageLabel: 'Persönliche Nachricht (optional)',
    messagePlaceholder: 'Ein paar Zeilen an den Kunden …',
    messageHelper:
      'Wird in der E-Mail über dem Button angezeigt. Leer lassen, wenn nur der Standardtext gehen soll.',
    unsubscribed:
      'Der Kunde hat E-Mails abbestellt. Es kann keine Benachrichtigung gesendet werden — bitte auf anderem Weg informieren.',
    skip: 'Nicht senden',
    send: 'E-Mail senden',
    sent: 'E-Mail wurde gesendet',
    failed: 'Die E-Mail konnte nicht gesendet werden.',
    // Resolved by `extractErrorMessage` from the `code` the server sends.
    errors: {
      noCustomer: 'Dem Projekt ist kein Kunde mehr zugewiesen.',
      customerChanged:
        'Das Projekt gehört inzwischen einem anderen Kunden. Es wurde nichts gesendet.',
      stateChanged:
        'Der Stand des Projekts hat sich seitdem geändert. Es wurde nichts gesendet.',
      unsubscribed:
        'Der Kunde hat E-Mails abbestellt. Es wurde nichts gesendet.',
      sendFailed: 'Der Mailversand hat nicht geklappt. Bitte später erneut.',
    },
  },
  onlineExpose: 'Online Exposé',
  /** Heads the public list. What it lists are objects on offer, each with an
   *  exposé — not a portfolio of finished work. */
  publicOverviewTitle: 'Saniert zum Festpreis',
  publicOverviewSubtitle:
    'Häuser und Wohnungen, die wir für dich sanieren und schlüsselfertig übergeben — zu einem garantierten Festpreis, der Kaufpreis und Sanierungskosten bereits vollständig enthält. Dein Vorteil: Die Kaufnebenkosten fallen nur auf den Kaufpreis der Immobilie an, nicht auf die Sanierungskosten.',
  /** Under a list that is showing only its first page. */
  publicOverviewMore: 'Weitere Objekte anzeigen',
  /** The public list calls them Objekte throughout, so its empty state does
   *  too — `noProjectsFound` is the internal list's word for the same miss. */
  publicOverviewEmpty: `Keine ${TERMS.propertyPlural} gefunden`,
  /** Closes the list: what to do when none of the objects is the one. */
  publicOverviewCta: {
    eyebrow: 'Nichts Passendes dabei?',
    headline: 'Dann suchen wir es.\nOder sanieren, was du schon hast.',
    /** `advisor` is the contact person's first name, or null where there is
     *  no calendar to book — the sentence then simply starts at the ask. */
    body: (advisor: string | null) =>
      advisor
        ? `Buche direkt einen Termin bei ${advisor} – oder sag uns, wonach du suchst. Wir werden aktiv für dich und machen dir Vorschläge. Und falls du schon eine Immobilie besitzt: Auch die sanieren wir dir zum Festpreis.`
        : 'Sag uns, wonach du suchst. Wir werden aktiv für dich und machen dir Vorschläge. Und falls du schon eine Immobilie besitzt: Auch die sanieren wir dir zum Festpreis.',
  },
  /** What the list counts, in the number the count came back with. */
  unit: (n: number) => (n === 1 ? TERMS.property : TERMS.propertyPlural),
  /**
   * How a list of projects can be ordered. Whole sentences rather than a field
   * and a direction beside it: the adjective is inflected for the noun it
   * follows, so the two cannot be picked apart into separate controls.
   */
  sort: {
    newest: 'Neueste zuerst',
    priceAsc: 'Kaufpreis: niedrigster zuerst',
    priceDesc: 'Kaufpreis: höchster zuerst',
    areaDesc: 'Wohnfläche: größte zuerst',
    areaAsc: 'Wohnfläche: kleinste zuerst',
    roomsDesc: 'Zimmer: meiste zuerst',
  },
} as const
